import { formatRelativeTime } from '@/utils/formatRelativeTime'

import type { RobotPosition } from '../../../services/api/robot-positions-store'

/** One labelled reading in the details panel. */
export interface DetailRow {
  label: string
  value: string
  /** Set when the row carries a safety meaning worth colouring. */
  tone?: 'good' | 'warn' | 'bad'
}

/** A titled group of readings. Sections with no rows are dropped before render. */
export interface DetailSection {
  title: string
  rows: DetailRow[]
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null
}

/**
 * "Just now" / "12s ago" / "4 min ago" / "2 h ago".
 *
 * Relative rather than a clock time because the question an operator is asking is "is this
 * current?", and a timestamp makes them do the subtraction themselves.
 */
export function formatAge(fromMs: number, nowMs: number): string {
  // Delegates, so the console has ONE relative-time vocabulary. This used to be a second
  // implementation that disagreed with `formatRelativeTime` on the hour abbreviation and the
  // long-ago fallback, and both rendered on the same dashboard about the same device.
  return formatRelativeTime(new Date(fromMs), { precision: 'second', now: nowMs })
}

const WORN_YES: DetailRow = { label: 'Wearing', value: 'Yes — helmet on', tone: 'good' }
const WORN_NO: DetailRow = { label: 'Wearing', value: 'No — helmet removed', tone: 'bad' }

/**
 * Coerce the vendor's wear code, which arrives as a number or as a QUOTED number.
 *
 * The firmware mixes quoted and unquoted numerics for the same key — `toNumber` in
 * `libs/realtime/src/helmet/codec.ts` exists for exactly that reason.
 */
function vendorWearCode(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v

  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)

    return Number.isFinite(n) ? n : null
  }

  return null
}

/**
 * The vendor encodes wear state as `is_weared`: 2 = worn, 1 = not worn, 0 = unknown.
 *
 * The mapping is NOT the obvious 1 = true / 0 = false, and reading it that way inverts the
 * safety-critical answer — "not wearing" would display as worn. Decoded in one named place so it
 * cannot be re-guessed at a call site.
 *
 * THREE shapes reach here, and reading only the number is what made this row invisible in
 * production. `libs/realtime/src/helmet/telemetry.ts::toHelmetTelemetry` runs `parseWorn` BEFORE
 * the frame goes on the wire, so what a live helmet actually delivers is `boolean | null` — and
 * `num(true)` is null, so the most safety-critical row on the card was silently dropped for every
 * real device while the unit tests, which fed the raw vendor `2`, went on passing.
 */
export function describeWorn(worn: unknown): DetailRow | null {
  if (worn === true) return WORN_YES
  if (worn === false) return WORN_NO

  const v = vendorWearCode(worn)

  // 0 is the vendor's explicit "unknown" and must stay absent rather than become "not worn".
  if (v === 2) return WORN_YES
  if (v === 1) return WORN_NO

  return null
}

function batteryTone(pct: number): DetailRow['tone'] {
  if (pct <= 15) return 'bad'
  if (pct <= 30) return 'warn'

  return 'good'
}

/** Format a coordinate pair the way a person reads one off a map. */
export function formatPosition(lat: number, lng: number): string {
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`
}

function push(rows: DetailRow[], label: string, value: string | null, tone?: DetailRow['tone']): void {
  if (value === null) return

  rows.push(tone ? { label, value, tone } : { label, value })
}

export interface DetailInputs {
  position?: RobotPosition
  telemetry?: Record<string, unknown>
  /** When the browser received that telemetry. */
  telemetryReceivedAt?: number
  now?: number
}

/**
 * Group the selected device's readings for display.
 *
 * Every field is optional on purpose: a robot and a helmet report different things, a helmet with
 * no wristband paired reports no vitals, and a frame can arrive half-formed. A missing reading is
 * omitted rather than rendered as "0" or "unknown" — an invented zero for a heart rate, a gas level
 * or a battery percentage is worse than an absent row, because it reads as a measurement.
 *
 * Undocumented vendor enums (`net_type`, `sim_status`, `online_type`, …) are shown as the raw value
 * the device sent, never as a guessed label. The protocol reference gives no mapping for them, and
 * inventing one would put a confident-looking word on screen that nothing backs.
 */
export function buildRobotDetailSections({
  position,
  telemetry,
  telemetryReceivedAt,
  now = Date.now(),
}: DetailInputs): DetailSection[] {
  const t = telemetry ?? {}

  // ---- Status -------------------------------------------------------------
  const status: DetailRow[] = []

  if (telemetryReceivedAt) push(status, 'Last update', formatAge(telemetryReceivedAt, now))

  const recordedAt = position?.recordedAt ? Date.parse(position.recordedAt) : NaN

  if (Number.isFinite(recordedAt)) push(status, 'Last seen', formatAge(recordedAt, now))

  const worn = describeWorn(t.worn)

  if (worn) status.push(worn)
  if (t.charging === true) push(status, 'Charging', 'Yes')

  // ---- Position -----------------------------------------------------------
  const pos: DetailRow[] = []

  if (position && Number.isFinite(position.lat) && Number.isFinite(position.lng)) {
    push(pos, 'Location', formatPosition(position.lat, position.lng))
  }

  const altitude = num(t.altitude)

  if (altitude !== null) push(pos, 'Altitude', `${Math.round(altitude)} m`)

  const accuracy = num(t.accuracy)

  // Wide error circles are the usual reason a marker sits somewhere surprising.
  if (accuracy !== null) push(pos, 'Accuracy', `±${Math.round(accuracy)} m`, accuracy > 50 ? 'warn' : undefined)

  const speed = num(t.speed)

  if (speed !== null) push(pos, 'Speed', `${speed} m/s`)

  const sats = num(t.satellites)

  if (sats !== null) push(pos, 'GPS satellites', String(sats), sats < 4 ? 'warn' : 'good')

  const heading = num(position?.heading)

  if (heading !== null) push(pos, 'Heading', `${Math.round(heading)}°`)

  // ---- Wearer -------------------------------------------------------------
  const wearer: DetailRow[] = []
  const vitals = (t.vitals ?? null) as Record<string, unknown> | null

  if (vitals) {
    const hr = num(vitals.heartRate)

    // Resting adult range; outside it is worth an operator's eye, not an alarm on its own.
    if (hr !== null) push(wearer, 'Heart rate', `${hr} bpm`, hr < 50 || hr > 120 ? 'warn' : 'good')

    // `systolic` is the decoded name; `shrink` is the vendor's own key, kept for a raw frame.
    const sys = num(vitals.systolic) ?? num(vitals.shrink)
    const dia = num(vitals.diastolic)

    if (sys !== null) push(wearer, 'Blood pressure', dia !== null ? `${sys}/${dia}` : `${sys}`)

    // Body temperature is a DECODED vital the vendor raises its own `body_temperature` alarm for,
    // and it was not being shown at all — while the undocumented motherboard temperature was.
    const bodyTemp = num(vitals.bodyTemperature)

    if (bodyTemp !== null) {
      push(wearer, 'Body temp', `${bodyTemp.toFixed(1)}°C`, bodyTemp < 35 || bodyTemp > 38 ? 'warn' : 'good')
    }

    const wristTemp = num(vitals.wristTemperature)

    if (wristTemp !== null) push(wearer, 'Wrist temp', `${wristTemp.toFixed(1)}°C`)

    // Not a field on `HelmetVitals` — the CAPS protocol has no SpO2 — but kept for wristband models
    // that do report one, since an absent field simply omits the row.
    const spo2 = num(vitals.bloodOxygen)

    if (spo2 !== null) push(wearer, 'Blood oxygen', `${spo2}%`, spo2 < 95 ? 'warn' : 'good')

    // The wristband battery is emitted as `power`. Reading `battery` meant this row never rendered.
    const wristband = num(vitals.power) ?? num(vitals.battery)

    if (wristband !== null) push(wearer, 'Wristband battery', `${wristband}%`)
  }

  // `stepCount` at the top level is the decoded name; the wristband's own `steps` is the fallback.
  const steps = num(t.stepCount) ?? num(vitals?.steps)

  if (steps !== null) push(wearer, 'Steps', String(steps))

  // ---- Environment --------------------------------------------------------
  const env: DetailRow[] = []

  push(env, 'Gas', str(t.gasData))
  push(env, 'Oxygen', str(t.oxygenData))
  // Raw on purpose: the vendor documents no encoding for `tempdata`, and a scaled number here
  // would be a fabricated temperature.
  push(env, 'Board temp (raw)', str(t.boardTemperatureRaw))

  // ---- Device -------------------------------------------------------------
  const device: DetailRow[] = []
  const battery = num(t.battery) ?? num(position?.battery)

  if (battery !== null) push(device, 'Battery', `${Math.round(battery)}%`, batteryTone(battery))

  const mv = num(t.batteryMv)

  if (mv !== null) push(device, 'Battery voltage', `${mv} mV`)

  push(device, 'Device ID', str(t.deviceId))

  const hangerA = num(t.hangerABattery)
  const hangerB = num(t.hangerBBattery)

  if (hangerA !== null) push(device, 'Hanger A battery', `${hangerA}%`)
  if (hangerB !== null) push(device, 'Hanger B battery', `${hangerB}%`)

  // ---- Connectivity -------------------------------------------------------
  const net: DetailRow[] = []
  const signal = num(t.netStrength)

  if (signal !== null) push(net, 'Signal', String(signal), signal <= 1 ? 'warn' : undefined)

  // Undocumented enums — shown as sent, never as a guessed label.
  const netType = num(t.netType)

  if (netType !== null) push(net, 'Network type', String(netType))

  const simData = num(t.simDataMb)

  if (simData !== null) push(net, 'SIM data', `${simData} MB`)

  const sim = num(t.simStatus)

  if (sim !== null) push(net, 'SIM status', String(sim))

  const card = num(t.memoryCardStatus)

  if (card !== null) push(net, 'Memory card', String(card))

  return [
    { title: 'Status', rows: status },
    { title: 'Position', rows: pos },
    { title: 'Wearer', rows: wearer },
    { title: 'Environment', rows: env },
    { title: 'Device', rows: device },
    { title: 'Connectivity', rows: net },
  ].filter(s => s.rows.length > 0)
}

/** Flat view, kept for callers that just want every row in order. */
export function buildRobotDetailRows(
  position: RobotPosition | undefined,
  telemetry: Record<string, unknown> | undefined,
): DetailRow[] {
  return buildRobotDetailSections({ position, telemetry }).flatMap(s => s.rows)
}
